import { create } from 'zustand'
import { produce } from 'immer'
import {
  ButtonColorProperty,
  ButtonStyleSelection,
  ColorSelection,
  DEFAULT_BUTTON_DATA,
  DEFAULT_COLOR_DATA,
  DEFAULT_FONT_DATA,
  FontSelection,
  LogoSelection,
} from './types'
import { LogoDisplay } from '@web/components/atoms/Logo'
import { useShallow } from 'zustand/react/shallow'

export enum Sections {
  Logo = 'Logo',
  Fonts = 'Font',
  Colors = 'Color',
  Buttons = 'Buttons',
}

type BrandingValues = string | FontSelection | ColorSelection | ButtonStyleSelection

export type Branding = {
  id?: string
  name: string | null
  logoData: LogoSelection
  fontData: FontSelection
  colorData: ColorSelection
  buttonData: ButtonStyleSelection
}

type BrandingActions = {
  loadBranding: (branding: Branding | null) => void
  handleChange: (name: string, value: BrandingValues) => void
  updateButtonColor: (name: string, color: string, key: ButtonColorProperty) => void
  updateLogoUrl: (url: string, style: LogoDisplay) => void
}

export type BrandingsState = Branding & {
  hasLoaded: boolean
  draft: Branding
  draftActions: BrandingActions
  actions: BrandingActions
}

export const DEFAULT_BRANDING_DATA = {
  fontData: DEFAULT_FONT_DATA,
  colorData: DEFAULT_COLOR_DATA,
  buttonData: DEFAULT_BUTTON_DATA,
  logoData: {
    [LogoDisplay.Circle]: '',
    [LogoDisplay.Rectangle]: '',
    [LogoDisplay.None]: '',
    [LogoDisplay.Custom]: '',
    [LogoDisplay.Primary]: '',
    [LogoDisplay.Reversed]: '',
  },
}

export const useBrandingsStore = create<BrandingsState>((set) => ({
  logoURL: '',
  hasLoaded: false,
  name: '',
  ...DEFAULT_BRANDING_DATA,
  draft: {
    name: '',
    ...DEFAULT_BRANDING_DATA,
  },
  draftActions: {
    loadBranding: (branding: Branding | null) =>
      set(
        produce((state) => {
          if (!branding) {
            state.draft = {
              name: '',
              ...DEFAULT_BRANDING_DATA,
            }
          }

          state.draft = branding
        }),
      ),
    handleChange: (name: string, value: BrandingValues) =>
      set(
        produce((state) => {
          state.draft = {
            ...state.draft,
            [name]: value,
          }
        }),
      ),
    updateButtonColor: (name: string, color: string, key: ButtonColorProperty) =>
      set(
        produce((state: BrandingsState) => {
          const buttonColor = state.draft.buttonData.buttonColors[name]

          switch (key) {
            case ButtonColorProperty.bgColor: {
              if (buttonColor.bgColor === buttonColor.outlineColor) {
                buttonColor.outlineColor = color
              }
              buttonColor.bgColor = color
              break
            }
            case ButtonColorProperty.hoverBgColor: {
              if (buttonColor.hoverBgColor === buttonColor.hoverOutlineColor) {
                buttonColor.hoverOutlineColor = color
              }
              buttonColor.hoverBgColor = color
              break
            }
            case ButtonColorProperty.textColor: {
              buttonColor.textColor = color
              break
            }
            case ButtonColorProperty.hoverTextColor: {
              buttonColor.hoverTextColor = color
              break
            }
            case ButtonColorProperty.outlineColor: {
              buttonColor.outlineColor = color
              break
            }
            case ButtonColorProperty.hoverOutlineColor: {
              buttonColor.hoverOutlineColor = color
              break
            }
          }
        }),
      ),
    updateLogoUrl: (url: string, style: LogoDisplay) =>
      set(
        produce((state: BrandingsState) => {
          if (!state.logoData) {
            state.logoData = { ...DEFAULT_BRANDING_DATA.logoData, [style]: url }
          } else {
            state.logoData[style] = url
          }
        }),
      ),
  },
  actions: {
    loadBranding: (branding: Branding | null) =>
      set(() => {
        return {
          ...(branding ?? {
            logoURL: '',
            fontData: DEFAULT_FONT_DATA,
            colorData: DEFAULT_COLOR_DATA,
            buttonData: DEFAULT_BUTTON_DATA,
            hasLoaded: true,
          }),
          hasLoaded: true,
        }
      }),
    handleChange: (name: string, value: BrandingValues) =>
      set((state) => {
        return {
          ...state,
          [name]: value,
        }
      }),
    updateButtonColor: (name: string, color: string, key: ButtonColorProperty) =>
      set(
        produce((state: BrandingsState) => {
          const buttonColor = state.buttonData.buttonColors[name]

          switch (key) {
            case ButtonColorProperty.bgColor: {
              if (buttonColor.bgColor === buttonColor.outlineColor) {
                buttonColor.outlineColor = color
              }
              buttonColor.bgColor = color
              break
            }
            case ButtonColorProperty.hoverBgColor: {
              if (buttonColor.hoverBgColor === buttonColor.hoverOutlineColor) {
                buttonColor.hoverOutlineColor = color
              }
              buttonColor.hoverBgColor = color
              break
            }
            case ButtonColorProperty.textColor: {
              buttonColor.textColor = color
              break
            }
            case ButtonColorProperty.hoverTextColor: {
              buttonColor.hoverTextColor = color
              break
            }
            case ButtonColorProperty.outlineColor: {
              buttonColor.outlineColor = color
              break
            }
            case ButtonColorProperty.hoverOutlineColor: {
              buttonColor.hoverOutlineColor = color
              break
            }
          }
        }),
      ),
    updateLogoUrl: (url: string, style: LogoDisplay) =>
      set(
        produce((state: BrandingsState) => {
          if (!state.logoData) {
            state.logoData = { ...DEFAULT_BRANDING_DATA.logoData, [style]: url }
          } else {
            state.logoData[style] = url
          }
        }),
      ),
  },
}))

export const useBrandingValues = () => {
  const branding = useBrandingsStore(
    useShallow(({ buttonData, colorData, fontData, logoData }) => ({
      [Sections.Logo]: logoData,
      [Sections.Fonts]: fontData,
      [Sections.Colors]: colorData,
      [Sections.Buttons]: buttonData,
    })),
  )
  return branding
}

const LEGACY_LOGO = [LogoDisplay.Circle, LogoDisplay.Rectangle]

export const useLogoValue = (display: LogoDisplay = LogoDisplay.Primary, url?: string): [LogoDisplay, string] => {
  const logoData = useBrandingsStore((state) => state.logoData)

  if (display === LogoDisplay.None) {
    return [LogoDisplay.None, '']
  }

  if (display === LogoDisplay.Custom) {
    return [LogoDisplay.Custom, url ?? '']
  }

  const displayToUse = LEGACY_LOGO.includes(display) ? LogoDisplay.Primary : display

  if (!logoData) {
    return [displayToUse, '']
  }

  // if it's primary but we haven't gotten a primary value fallback onto
  // rectangle and circle values
  if (display === LogoDisplay.Primary && !logoData[display]) {
    return [displayToUse, logoData[LogoDisplay.Rectangle] ?? logoData[LogoDisplay.Circle] ?? '']
  }

  // a bit confusing, but essentially grab the logo value, but display as primary (maybe)
  return [displayToUse, logoData[display]]
}
